import React, { useState, useRef } from 'react';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import FocusLock from 'react-focus-lock';
import { Subtitle, LinkGroup } from 'Styles/links';
import { generateColumns } from './NavFlyout.utils';

import * as Styled from './NavFlyout.styled';
import { convertToCamelCase } from '../NavDrawer/NavDrawer.utils';
import LinkText from '../../../LinkText/LinkText';

/**
 * A component that displays on hover of level 1 Navigation sections on Large + screen sizes.
 */
const NavFlyout = ({
  flyoutAnimationStyle,
  level1Section,
  onBackdropEnter,
  onKeyDown,
  onMouseEnter,
  onMouseLeave
}) => {
  // level1Section contains all of the flyout content, so if it's null, the flyout should not render at all
  if (!level1Section) return null;

  const positionRef = useRef(null);
  const [positionFromTop, setPositionFromTop] = useState(0);

  const getPositionFromTop = () => {
    const { top } = positionRef.current.getBoundingClientRect();
    setPositionFromTop(top);
  };

  const { sections, menuTileGroup } = level1Section;
  const hasLeftContainer = level1Section?.links;

  const dataLabelName = convertToCamelCase(level1Section?.text);

  return (
    <Styled.NavFlyout onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <FocusLock returnFocus>
        <Styled.LayoutContainer
          ref={positionRef}
          onMouseEnter={getPositionFromTop}
          positionFromTop={positionFromTop}
          data-label={`navFlyout_${dataLabelName}`}
        >
          <Styled.Layout onKeyDown={onKeyDown}>
            {hasLeftContainer && (
              <Styled.LeftContainer
                $flyoutAnimationStyle={flyoutAnimationStyle}
              >
                {level1Section.links
                  ?.filter((level3Section) => !level3Section.hideOnDesktop)
                  .map((level3Section) => (
                    <React.Fragment key={level3Section.id}>
                      {level3Section.text && (
                        <Subtitle>{level3Section.text}</Subtitle>
                      )}
                      <LinkGroup variant="mini" paddingSize="lg">
                        {level3Section.links
                          ?.filter((link) => !link.hideOnDesktop)
                          .map((link) => (
                            <LinkText
                              as={
                                link.url && !link.url.includes('/help')
                                  ? NextLink
                                  : LinkText
                              }
                              href={link.url}
                              color={link.color}
                              size="Mini"
                              data-label="navLink_L3"
                              key={link.id}
                            >
                              {link.text}
                            </LinkText>
                          ))}
                      </LinkGroup>
                    </React.Fragment>
                  ))}
              </Styled.LeftContainer>
            )}
            <Styled.RightContainer hasLeftContainer={hasLeftContainer}>
              {generateColumns({
                sections: sections || [],
                menuTileGroup: menuTileGroup || [],
                level1Section,
                flyoutAnimationStyle
              })}
            </Styled.RightContainer>
          </Styled.Layout>
        </Styled.LayoutContainer>
      </FocusLock>
      <Styled.Backdrop onMouseEnter={onBackdropEnter} />
    </Styled.NavFlyout>
  );
};

NavFlyout.propTypes = {
  /**
   * Controls content entering animations: 'piano', 'fade' or 'none', default is 'none' (shows no animation)
   */
  flyoutAnimationStyle: PropTypes.oneOf(['piano', 'fade', 'none']),
  /**
   *  Individual Level 1 Navigation Sections (Women, Men, Kids, About Us, Help)
   *  This prop determines if the NavFlyout is open.
   */
  level1Section: PropTypes.shape({
    id: PropTypes.string,
    url: PropTypes.string,
    text: PropTypes.string,
    name: PropTypes.string,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        color: PropTypes.string,
        id: PropTypes.string,
        url: PropTypes.string,
        text: PropTypes.string
      })
    ),
    sections: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        url: PropTypes.string,
        text: PropTypes.string,
        name: PropTypes.string,
        sections: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            name: PropTypes.string,
            text: PropTypes.string,
            links: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.string,
                url: PropTypes.string,
                text: PropTypes.string
              })
            )
          })
        )
      })
    ),
    menuTileGroup: PropTypes.shape({
      type: PropTypes.string,
      tiles: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string,
          text: PropTypes.string,
          url: PropTypes.string,
          image: PropTypes.shape({
            file: PropTypes.shape({
              url: PropTypes.string,
              details: PropTypes.shape({
                image: PropTypes.shape({
                  height: PropTypes.number,
                  width: PropTypes.number
                })
              })
            })
          })
        })
      )
    })
  }),
  /**
   * Handles closing of Navigation Flyout and Scroll Lock when cursor leaves component
   */
  onBackdropEnter: PropTypes.func,
  /**
   * Handles closing of Navigation Flyout and Scroll Lock when the Escape key is pressed
   */
  onKeyDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};

NavFlyout.defaultProps = {
  flyoutAnimationStyle: 'none',
  level1Section: null,
  onBackdropEnter: null,
  onKeyDown: null,
  onMouseEnter: null,
  onMouseLeave: null
};

export default NavFlyout;
